import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import DefaultWhiteColumn from '~/features/base/components/DefaultWhiteColumn';
import StatsWidgets from '@rio-cloud/rio-uikit/StatsWidgets';
import CampaignCard from './CampaignCard';
import CampaignCardSkeleton from './CampaignCardSkeleton';
import FilterSearchBar from './FilterSearchBar';
import CampaignStatusTabs from './CampaignStatusTabs';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import CreateCampaignFormDialog from '~/features/campaigns/components/campaignForm/CreateCampaignFormDialog';
import { toShortSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import { Campaign, CampaignStatus } from '~/api/models/campaign';
import { ALL, allItem } from '~/features/base/constants/filterOptions';
import { DETAILS_PATH } from '~/features/base/constants/routes';
import { useListCampaignsQuery } from '~/api/campaigns/Campaign.api';

const IN_PROGRESS: CampaignStatus = 'IN_PROGRESS';

const CampaignListContainer: React.FC = ({ match }) => {
    const history = useHistory();

    const [searchTerm, setSearchTerm] = useState<string | undefined>();
    const [deviceTypeFilter, setDeviceTypeFilter] = useState(allItem);
    const [deliverableTypeFilter, setDeliverableTypeFilter] = useState(ALL);
    const [showDialog, setShowDialog] = useState(false);
    const [filteredResults, setFilteredResults] = useState<Record<CampaignStatus, Campaign[]>>({});

    const { data: campaignList, isLoading } = useListCampaignsQuery({});

    const campaignsInProgress = campaignList?.find(c => c.status === IN_PROGRESS);
    const [statusSelected, setStatusSelected] = useState(campaignsInProgress ? IN_PROGRESS : ALL);

    const groupCampaignsByStatus = (campaigns: Campaign[]): Record<Campaign['status'], Campaign[]> => {
        if (!campaigns) {
            return {};
        }
        return campaigns.reduce((acc, campaign) => {
            if (!acc[campaign.status]) {
                acc[campaign.status] = [];
            }
            acc[campaign.status].push(campaign);
            return acc;
        }, {} as Record<Campaign['status'], Campaign[]>);
    };

    const allCampaigns = useMemo(() => {
        return campaignList ? groupCampaignsByStatus(campaignList) : {};
    }, [campaignList]);

    const searchCampaigns = (term: string) => {
        if (!campaignList) {
            return [];
        }
        const lowerTerm = term.toLowerCase();

        return campaignList.filter(c => {
            return c.name.toLowerCase().includes(lowerTerm) ||
                c.deviceType?.toLowerCase().includes(lowerTerm) ||
                c.deliverableId?.toLowerCase().includes(lowerTerm) ||
                c.deliverableType?.toLowerCase().includes(lowerTerm) ||
                toShortSemanticVersion(c.deliverableVersion).includes(lowerTerm);
        });
    };

    const getFilteredCampaigns = () => {
        if (!campaignList) {
            return {};
        }

        let filtered = searchTerm ? searchCampaigns(searchTerm) : campaignList;

        if (deviceTypeFilter.value !== ALL) {
            filtered = filtered.filter(
                campaign => campaign.deviceType?.toLowerCase() === deviceTypeFilter?.value.toLowerCase()
            );
        }

        if (deliverableTypeFilter !== ALL) {
            filtered = filtered.filter(
                campaign => campaign?.deliverableType === deliverableTypeFilter
            );
        }

        return groupCampaignsByStatus(filtered);
    };

    useEffect(() => {
        setFilteredResults(getFilteredCampaigns());
    }, [campaignList, searchTerm, deviceTypeFilter, deliverableTypeFilter]);

    const handleOnStatusSelection = (campaignStatus) => {
        setStatusSelected(campaignStatus);
    };

    const handleCampaignOnClick = (campaign) => {
        history.push({
            pathname: `${match.path}/${campaign.id}/${DETAILS_PATH}`,
            state: { campaign }
        });
    };

    const handleCreateCampaign = () => {
        setShowDialog(true);
    };

    const results = (statusSelected === ALL
            ? Object.values(filteredResults)
            : [filteredResults[statusSelected]]
    )?.flat() || [];

    return (
        <div>
            <div className='display-flex justify-content-between align-items-center padding-bottom-25'>
                <h1>
                    <FormattedMessage id={'intl-msg:campaigns'}/>
                </h1>
                <button type='button' className='btn btn-primary' onClick={handleCreateCampaign}>
                    <span className='rioglyph rioglyph-plus-light' aria-hidden='true'></span>
                    <FormattedMessage id={'intl-msg:campaign:create'}/>
                </button>
            </div>
            <CreateCampaignFormDialog
                show={showDialog}
                onHide={() => setShowDialog(false)}
                onSubmitSuccess={() => {
                }}
            />
            <DefaultWhiteColumn className='padding-50 clearfix height-100pct'>
                <div>
                    <FilterSearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        deviceTypeFilter={deviceTypeFilter}
                        setDeviceTypeFilter={setDeviceTypeFilter}
                        deliverableTypeFilter={deliverableTypeFilter}
                        setDeliverableTypeFilter={setDeliverableTypeFilter}
                    />
                    {campaignList && campaignList.length > 0 && (
                        <CampaignStatusTabs
                            statusSelected={statusSelected}
                            onStatusSelection={handleOnStatusSelection}
                            filteredResults={filteredResults}
                            allCampaigns={allCampaigns}
                        />
                    )}
                </div>
                <div className='row margin-top-25'>
                    {results.length === 0 && (
                        <NotFoundState
                            fullWidth
                            headline={<FormattedMessage id={'intl-msg:campaign:emptyState'}/>}
                            buttons={[{
                                text: (
                                    <>
                                        <span className='rioglyph rioglyph-plus-light' aria-hidden='true'></span>
                                        <span className='text-capitalize'>
                                            <FormattedMessage id={'intl-msg:campaign:create'}/>
                                        </span>
                                    </>
                                ),
                                onClick: handleCreateCampaign
                            }]}
                        />
                    )}
                    <div style={{ height: 'calc(100vh - 430px)', overflowY: 'auto' }}>
                        {isLoading ? (
                            <div className='row'>
                                <StatsWidgets className='gap-0'>
                                    {[1, 2, 3, 4, 5, 6].map(i => (
                                        <CampaignCardSkeleton key={i}/>
                                    ))}
                                </StatsWidgets>
                            </div>
                        ) : (
                            <StatsWidgets className='gap-0'>
                                {results.map((campaign, index) => (
                                    campaign && (
                                        <CampaignCard
                                            key={index}
                                            campaign={campaign}
                                            onClick={handleCampaignOnClick}
                                        />
                                    )
                                ))}
                            </StatsWidgets>
                        )}
                    </div>
                </div>
            </DefaultWhiteColumn>
        </div>
    );
};

export default CampaignListContainer;
